import * as THREE from 'three';
import type { Object3D } from 'three';
import { AbstractSketchEdgeObject } from '../../../mesh/sketch/AbstractSketchEdgeObject';
import {
	isSketchArcObject,
	isSketchCircleObject,
	isSketchEllipseObject,
	isSketchLineObject,
	isSketchParabolaObject,
	isSketchSplineObject,
	isSketchStyleSplineObject,
} from '../../../mesh/guards';
import type { SketchArcDefinition, SketchArcObject } from '../../../mesh/sketch/SketchArcObject';
import type { SketchCircleObject } from '../../../mesh/sketch/SketchCircleObject';
import type { SketchEllipseDefinition, SketchEllipseObject } from '../../../mesh/sketch/SketchEllipseObject';
import type { SketchLineObject } from '../../../mesh/sketch/SketchLineObject';
import type { SketchParabolaDefinition, SketchParabolaObject } from '../../../mesh/sketch/SketchParabolaObject';
import {
	SketchBezierCurveClosureKind,
	type SketchSplineObject,
} from '../../../mesh/sketch/SketchSplineObject';
import type { SketchStyleSplineObject } from '../../../mesh/sketch/SketchStyleSplineObject';
import type {
	ArcReferenceProjectionState,
	EllipseArcReferenceProjectionState,
	LineReferenceProjectionState,
	SketchEntityReferenceProjectionState,
	SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import type { ProjectionResult } from '../model/ProjectionResult';

/**
 * 投影几何修改分发器。
 *
 * 说明：
 * 1. 只判断目标对象能否原位更新，并按投影类型修改几何；
 * 2. 不访问仓储，也不决定事务、回滚和诊断策略；
 * 3. 几何类型分派集中后，单条重建流程无需了解每类实体的写入细节。
 */
export class SketchEntityReferenceProjectionMutationDispatcher {
	/** line same-kind 重建时用于判断投影载体退化的阈值。 */
	private readonly lineRebuildEpsilon = 1e-8;

	private toVector3(world: [number, number, number]): THREE.Vector3 {
		return new THREE.Vector3(world[0], world[1], world[2]);
	}

	/**
	 * 解析直线载体方向。
	 *
	 * @param startWorld 载体起点。
	 * @param endWorld 载体终点。
	 * @returns 非退化时返回单位方向，否则返回 null。
	 */
	private resolveLineCarrierDirection(startWorld: THREE.Vector3, endWorld: THREE.Vector3): THREE.Vector3 | null {
		const directionWorld = endWorld.clone().sub(startWorld);
		const directionLength = directionWorld.length();
		if (directionLength <= this.lineRebuildEpsilon) {
			return null;
		}

		return directionWorld.divideScalar(directionLength);
	}

	/**
	 * 计算点在直线载体上的有符号标量。
	 *
	 * @param pointWorld 点世界坐标。
	 * @param originWorld 载体原点。
	 * @param directionWorld 载体单位方向。
	 * @returns 有符号标量。
	 */
	private resolveLineScalar(pointWorld: THREE.Vector3, originWorld: THREE.Vector3, directionWorld: THREE.Vector3): number {
		return pointWorld.clone().sub(originWorld).dot(directionWorld);
	}

	/**
	 * 判断两个标量是否可视为相等。
	 *
	 * @param left 第一个标量。
	 * @param right 第二个标量。
	 * @returns 差值小于重建阈值时返回 true。
	 */
	private areLineScalarsNear(left: number, right: number): boolean {
		return Math.abs(left - right) <= this.lineRebuildEpsilon;
	}

	/**
	 * 解析投影线载体长度。
	 *
	 * @param projectionResult line 投影结果。
	 * @returns 非退化长度。
	 */
	private resolveLineProjectionLength(projectionResult: Extract<ProjectionResult, { projectionResultKind: 'line' }>): number {
		return this.toVector3(projectionResult.geometryPayload.startWorld).distanceTo(this.toVector3(projectionResult.geometryPayload.endWorld));
	}

	/**
	 * 判断 line 投影状态是否覆盖旧投影整段。
	 *
	 * 设计说明：
	 * 旧 topology-edge 记录没有 `attachmentMode`，只能通过旧 payload 长度和标量范围反推；
	 * 这里允许正向与反向两种端点顺序，避免重建时破坏目标线端点的原有方向语义。
	 *
	 * @param projectionState line 投影状态。
	 * @param projectionLength 旧投影载体长度。
	 * @returns 覆盖整段时返回 true。
	 */
	private isFullLineProjectionRange(projectionState: LineReferenceProjectionState, projectionLength: number): boolean {
		if (!Number.isFinite(projectionLength) || projectionLength <= this.lineRebuildEpsilon) {
			return false;
		}

		const matchesForward = this.areLineScalarsNear(projectionState.startScalar, 0) && this.areLineScalarsNear(projectionState.endScalar, projectionLength);
		const matchesBackward = this.areLineScalarsNear(projectionState.endScalar, 0) && this.areLineScalarsNear(projectionState.startScalar, projectionLength);

		return matchesForward || matchesBackward;
	}

	/**
	 * 解析本轮 line 重建应采用的附着模式。
	 *
	 * @param reference 引用记录。
	 * @param projectionState 当前可用的 line 投影状态。
	 * @returns line 附着模式。
	 */
	private resolveLineProjectionAttachmentMode(
		reference: SketchEntityReferenceRecord,
		projectionState: LineReferenceProjectionState
	): NonNullable<LineReferenceProjectionState['attachmentMode']> {
		if (projectionState.attachmentMode) {
			return projectionState.attachmentMode;
		}

		if (reference.sourceDescriptor.kind === 'topology-edge' && reference.rebuildPayload.projectionResultKind === 'line') {
			const previousStartWorld = this.toVector3(reference.rebuildPayload.geometryPayload.startWorld);
			const previousEndWorld = this.toVector3(reference.rebuildPayload.geometryPayload.endWorld);
			const previousProjectionLength = previousStartWorld.distanceTo(previousEndWorld);
			if (this.isFullLineProjectionRange(projectionState, previousProjectionLength)) {
				return 'full-entity';
			}
		}

		return 'partial-range';
	}

	/**
	 * 生成当前投影整段对应的 line 投影状态。
	 *
	 * @param projectionResult 当前 line 投影结果。
	 * @param previousState 上一次投影状态，用于保留端点方向。
	 * @returns 可持久化的整边投影状态。
	 */
	private createFullLineProjectionState(
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'line' }>,
		previousState: LineReferenceProjectionState
	): LineReferenceProjectionState {
		const projectionLength = this.resolveLineProjectionLength(projectionResult);
		const isBackward = previousState.startScalar > previousState.endScalar;

		return {
			projectionResultKind: 'line',
			startScalar: isBackward ? projectionLength : 0,
			endScalar: isBackward ? 0 : projectionLength,
			attachmentMode: 'full-entity',
		};
	}

	/**
	 * 从引用记录或当前目标对象解析 line 附着状态。
	 * 说明：
	 * 1. 新记录优先使用持久化 `projectionState`；
	 * 2. 老记录没有该字段时，尽量用旧 rebuild payload 的投影载体反推；
	 * 3. 若旧载体也退化，则退回当前目标线段自身长度，避免 rebuild 因迁移数据中断。
	 *
	 * @param reference 引用记录。
	 * @param targetLine 目标直线。
	 * @returns line 附着状态。
	 */
	private resolveLineProjectionState(reference: SketchEntityReferenceRecord, targetLine: SketchLineObject): LineReferenceProjectionState {
		if (reference.projectionState?.projectionResultKind === 'line') {
			return reference.projectionState;
		}

		if (reference.rebuildPayload.projectionResultKind === 'line') {
			const previousStartWorld = this.toVector3(reference.rebuildPayload.geometryPayload.startWorld);
			const previousEndWorld = this.toVector3(reference.rebuildPayload.geometryPayload.endWorld);
			const previousDirectionWorld = this.resolveLineCarrierDirection(previousStartWorld, previousEndWorld);
			if (previousDirectionWorld) {
				return {
					projectionResultKind: 'line',
					startScalar: this.resolveLineScalar(targetLine.startPoint.getPosition(), previousStartWorld, previousDirectionWorld),
					endScalar: this.resolveLineScalar(targetLine.endPoint.getPosition(), previousStartWorld, previousDirectionWorld),
				};
			}
		}

		return {
			projectionResultKind: 'line',
			startScalar: 0,
			endScalar: targetLine.startPoint.getPosition().distanceTo(targetLine.endPoint.getPosition()),
		};
	}

	/**
	 * 从引用记录或当前目标对象解析 arc 附着状态。
	 *
	 * @param reference 引用记录。
	 * @param targetArc 目标圆弧。
	 * @returns arc 附着状态。
	 */
	private resolveArcProjectionState(reference: SketchEntityReferenceRecord, targetArc: SketchArcObject): ArcReferenceProjectionState {
		if (reference.projectionState?.projectionResultKind === 'arc') {
			return reference.projectionState;
		}

		const definition = targetArc.getArcDefinition();
		return {
			projectionResultKind: 'arc',
			startAngle: definition.startAngle,
			endAngle: definition.endAngle,
			clockwise: definition.clockwise,
		};
	}

	/**
	 * 从引用记录或当前目标对象解析 ellipse-arc 附着状态。
	 *
	 * @param reference 引用记录。
	 * @param targetEllipse 目标椭圆弧。
	 * @returns ellipse-arc 附着状态。
	 */
	private resolveEllipseArcProjectionState(reference: SketchEntityReferenceRecord, targetEllipse: SketchEllipseObject): EllipseArcReferenceProjectionState {
		if (reference.projectionState?.projectionResultKind === 'ellipse-arc') {
			return reference.projectionState;
		}

		const definition = targetEllipse.getEllipseDefinition();
		return {
			projectionResultKind: 'ellipse-arc',
			startAngle: definition.startAngle,
			endAngle: definition.endAngle,
			clockwise: definition.clockwise,
		};
	}

	/**
	 * 原位应用 line 投影结果。
	 *
	 * @param targetLine 目标直线。
	 * @param projectionResult 最新投影结果。
	 */
	private applyLineProjectionInPlace(targetLine: SketchLineObject, projectionResult: Extract<ProjectionResult, { projectionResultKind: 'line' }>): void {
		targetLine.startPoint.syncPositionSilently(this.toVector3(projectionResult.geometryPayload.startWorld));
		targetLine.endPoint.syncPositionSilently(this.toVector3(projectionResult.geometryPayload.endWorld));
		this.refreshLineProjectionGeometrySilently(targetLine);
	}

	/**
	 * 按整边语义原位应用 line 投影结果。
	 *
	 * @param targetLine 目标直线。
	 * @param projectionResult 最新 line 投影。
	 * @param projectionState 当前 line 投影状态，用于保留正反向端点顺序。
	 */
	private applyFullLineProjectionInPlace(
		targetLine: SketchLineObject,
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'line' }>,
		projectionState: LineReferenceProjectionState
	): void {
		const startWorld = this.toVector3(projectionResult.geometryPayload.startWorld);
		const endWorld = this.toVector3(projectionResult.geometryPayload.endWorld);
		const isBackward = projectionState.startScalar > projectionState.endScalar;

		targetLine.startPoint.syncPositionSilently(isBackward ? endWorld : startWorld);
		targetLine.endPoint.syncPositionSilently(isBackward ? startWorld : endWorld);
		this.refreshLineProjectionGeometrySilently(targetLine);
	}

	/**
	 * 按持久化投影状态原位应用 line same-kind 重建。
	 *
	 * @param targetLine 目标直线。
	 * @param projectionResult 最新 line 投影。
	 * @param projectionState 目标线段在投影载体上的附着状态。
	 */
	private applyLineProjectionStateInPlace(
		targetLine: SketchLineObject,
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'line' }>,
		projectionState: LineReferenceProjectionState
	): void {
		if (projectionState.attachmentMode === 'full-entity') {
			this.applyFullLineProjectionInPlace(targetLine, projectionResult, projectionState);
			return;
		}

		const originWorld = this.toVector3(projectionResult.geometryPayload.startWorld);
		const endWorld = this.toVector3(projectionResult.geometryPayload.endWorld);
		const directionWorld = this.resolveLineCarrierDirection(originWorld, endWorld);
		if (!directionWorld) {
			throw new Error('line same-kind 重建失败：最新投影直线退化。');
		}

		targetLine.startPoint.syncPositionSilently(originWorld.clone().addScaledVector(directionWorld, projectionState.startScalar));
		targetLine.endPoint.syncPositionSilently(originWorld.clone().addScaledVector(directionWorld, projectionState.endScalar));
		this.refreshLineProjectionGeometrySilently(targetLine);
	}

	/**
	 * 刷新转换实体直线的渲染几何，但不把派生中点同步误报为独立定义变更。
	 *
	 * 直线端点已经由本分发器作为同一引用候选批量写入；中点只是端点的派生显示点，
	 * 必须先静默对齐。这样 `SketchLineObject.updateGeometry()` 内部再次同步中点时会成为
	 * 无变化操作，最终仅由引用批次的 `target-rebuilt` 提交推进一次 definition revision。
	 *
	 * @param targetLine 当前转换实体目标直线。
	 */
	private refreshLineProjectionGeometrySilently(targetLine: SketchLineObject): void {
		targetLine.getMidPoint().syncPositionSilently(targetLine.getMidpointPosition());
		targetLine.updateGeometry();
	}

	/**
	 * 在转换实体刷新共享端点的相邻边前，静默对齐相邻直线的派生中点。
	 *
	 * 转换实体会按批次写入共享端点，并在每条目标边完成后刷新相邻边的渲染几何。
	 * 相邻直线的中点同样只是端点派生值，若不提前同步，其 `updateGeometry()` 会把中点
	 * 写入误报成独立定义修改，导致闭合 Wire 一轮重建产生多条未提交 revision。
	 *
	 * @param targetEntity 本轮已完成原位投影更新的目标边。
	 */
	private prepareConnectedLineNeighborGeometryRefresh(
		targetEntity: AbstractSketchEdgeObject,
	): void {
		const preparedNeighborIds = new Set<string>();
		targetEntity.getRelatedSketchPointsSnapshot().forEach(point => {
			point.getConnectedEdgesSnapshot().forEach(connectedEdge => {
				if (
					connectedEdge === targetEntity
					|| !isSketchLineObject(connectedEdge)
					|| preparedNeighborIds.has(connectedEdge.uuid)
				) {
					return;
				}
				preparedNeighborIds.add(connectedEdge.uuid);
				connectedEdge.getMidPoint().syncPositionSilently(
					connectedEdge.getMidpointPosition(),
				);
			});
		});
	}

	/**
	 * 原位应用 circle 投影结果。
	 *
	 * @param targetCircle 目标圆。
	 * @param projectionResult 最新投影结果。
	 */
	private applyCircleProjectionInPlace(
		targetCircle: SketchCircleObject,
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'circle' }>
	): void {
		targetCircle.getCenterControlPoint().syncPositionSilently(this.toVector3(projectionResult.geometryPayload.centerWorld));
		targetCircle.setRadiusSilently(projectionResult.geometryPayload.radius);
	}

	/**
	 * 原位应用 arc 投影结果。
	 *
	 * @param targetArc 目标圆弧。
	 * @param projectionResult 最新投影结果。
	 */
	private applyArcProjectionInPlace(targetArc: SketchArcObject, projectionResult: Extract<ProjectionResult, { projectionResultKind: 'arc' }>): void {
		const nextDefinition: SketchArcDefinition = {
			centerWorld: this.toVector3(projectionResult.geometryPayload.centerWorld),
			radius: projectionResult.geometryPayload.radius,
			startAngle: projectionResult.geometryPayload.startAngle,
			endAngle: projectionResult.geometryPayload.endAngle,
			clockwise: projectionResult.geometryPayload.clockwise,
		};
		targetArc.updateArcDefinition(nextDefinition);
		targetArc.syncControlPointsFromDefinition();
	}

	/**
	 * 按持久化投影状态原位应用 arc same-kind 重建。
	 *
	 * @param targetArc 目标圆弧。
	 * @param projectionResult 最新 arc 投影。
	 * @param projectionState 目标弧段在投影载体上的参数角状态。
	 */
	private applyArcProjectionStateInPlace(
		targetArc: SketchArcObject,
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'arc' }>,
		projectionState: ArcReferenceProjectionState
	): void {
		const nextDefinition: SketchArcDefinition = {
			centerWorld: this.toVector3(projectionResult.geometryPayload.centerWorld),
			radius: projectionResult.geometryPayload.radius,
			startAngle: projectionState.startAngle,
			endAngle: projectionState.endAngle,
			clockwise: projectionState.clockwise,
		};
		targetArc.updateArcDefinition(nextDefinition);
		targetArc.syncControlPointsFromDefinition();
	}

	/**
	 * 原位应用 ellipse / ellipse-arc 投影结果。
	 * 说明：
	 * 1. `ellipse` payload 只描述完整椭圆，不携带弧段角度；
	 * 2. `ellipse-arc` 才保留 start/end/clockwise 三元组；
	 * 3. 因此这里必须按 `projectionResultKind` 分开补齐定义，不能直接共用同一套字段映射。
	 *
	 * @param targetEllipse 目标椭圆对象。
	 * @param projectionResult 最新投影结果。
	 */
	private applyEllipseProjectionInPlace(
		targetEllipse: SketchEllipseObject,
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'ellipse' | 'ellipse-arc' }>
	): void {
		const nextDefinition: SketchEllipseDefinition =
			projectionResult.projectionResultKind === 'ellipse'
				? {
						centerWorld: this.toVector3(projectionResult.geometryPayload.centerWorld),
						radiusX: projectionResult.geometryPayload.radiusX,
						radiusY: projectionResult.geometryPayload.radiusY,
						rotation: projectionResult.geometryPayload.rotation,
						startAngle: 0,
						endAngle: Math.PI * 2,
						clockwise: false,
					}
				: {
						centerWorld: this.toVector3(projectionResult.geometryPayload.centerWorld),
						radiusX: projectionResult.geometryPayload.radiusX,
						radiusY: projectionResult.geometryPayload.radiusY,
						rotation: projectionResult.geometryPayload.rotation,
						startAngle: projectionResult.geometryPayload.startAngle,
						endAngle: projectionResult.geometryPayload.endAngle,
						clockwise: projectionResult.geometryPayload.clockwise,
					};
		targetEllipse.updateEllipseDefinition(nextDefinition);
		targetEllipse.syncControlPointsFromDefinition();
	}

	/**
	 * 按持久化投影状态原位应用 ellipse-arc same-kind 重建。
	 *
	 * @param targetEllipse 目标椭圆弧。
	 * @param projectionResult 最新 ellipse-arc 投影。
	 * @param projectionState 目标弧段在椭圆载体上的参数角状态。
	 */
	private applyEllipseArcProjectionStateInPlace(
		targetEllipse: SketchEllipseObject,
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'ellipse-arc' }>,
		projectionState: EllipseArcReferenceProjectionState
	): void {
		const nextDefinition: SketchEllipseDefinition = {
			centerWorld: this.toVector3(projectionResult.geometryPayload.centerWorld),
			radiusX: projectionResult.geometryPayload.radiusX,
			radiusY: projectionResult.geometryPayload.radiusY,
			rotation: projectionResult.geometryPayload.rotation,
			startAngle: projectionState.startAngle,
			endAngle: projectionState.endAngle,
			clockwise: projectionState.clockwise,
		};
		targetEllipse.updateEllipseDefinition(nextDefinition);
		targetEllipse.syncControlPointsFromDefinition();
	}

	/**
	 * 原位应用 parabola 投影结果。
	 * 说明：
	 * 1. 抛物线的稳定语义由焦点、顶点和起终参数表达；
	 * 2. same-kind 重建时应复用目标对象，避免替换对象导致下游引用和基础重合关系断开；
	 * 3. `SketchParabolaObject` 会在更新定义时同步 4 个控制点，因此这里不直接操作点对象。
	 *
	 * @param targetParabola 目标抛物线。
	 * @param projectionResult 最新抛物线投影。
	 */
	private applyParabolaProjectionInPlace(
		targetParabola: SketchParabolaObject,
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'parabola' }>
	): void {
		const nextDefinition: SketchParabolaDefinition = {
			focalWorld: this.toVector3(projectionResult.geometryPayload.focalWorld),
			apexWorld: this.toVector3(projectionResult.geometryPayload.apexWorld),
			startParameter: projectionResult.geometryPayload.startParameter,
			endParameter: projectionResult.geometryPayload.endParameter,
			segments: projectionResult.geometryPayload.segments,
		};
		targetParabola.updateParabolaDefinition(nextDefinition);
	}

	/**
	 * 原位应用普通样条投影结果。
	 *
	 * @param targetSpline 目标普通样条。
	 * @param projectionResult 最新普通样条投影。
	 */
	private applySplineProjectionInPlace(
		targetSpline: SketchSplineObject,
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'spline' }>
	): void {
		this.applySplineCurveDefinitionInPlace(
			targetSpline,
			projectionResult.geometryPayload.controlPointWorlds.map(world => this.toVector3(world)),
			projectionResult.geometryPayload.curvePointWorlds.map(world => this.toVector3(world)),
			projectionResult.geometryPayload.curveMetadata,
			projectionResult.geometryPayload.curveEvaluationDefinition,
		);
	}

	/**
	 * 原位应用样式样条投影结果。
	 *
	 * @param targetSpline 目标样式样条。
	 * @param projectionResult 最新样式样条投影。
	 */
	private applyStyleSplineProjectionInPlace(
		targetSpline: SketchStyleSplineObject,
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'style-spline' }>
	): void {
		this.applySplineCurveDefinitionInPlace(
			targetSpline,
			projectionResult.geometryPayload.controlPointWorlds.map(world => this.toVector3(world)),
			projectionResult.geometryPayload.curvePointWorlds.map(world => this.toVector3(world)),
			projectionResult.geometryPayload.curveMetadata
		);
	}

	/**
	 * 按样条曲线定义原位刷新目标对象。
	 * 说明：
	 * 1. 普通样条携带权威定义时，无论控制点数量是否变化都通过对象协议原子替换采样与极点；
	 * 2. 不携带权威定义且控制点数量不变时，直接同步坐标与采样点，避免异步 Worker 覆盖投影结果；
	 * 3. 控制点拓扑变化时仍回退到对象自身重建协议，保证结构正确。
	 *
	 * @param targetSpline 目标样条对象。
	 * @param controlPointPositions 控制点世界坐标，包含起点与终点。
	 * @param curvePoints 采样曲线点世界坐标。
	 * @param curveMetadata 曲线元数据。
	 * @param curveEvaluationDefinition 与当前投影一致的权威 B 样条定义。
	 */
	private applySplineCurveDefinitionInPlace(
		targetSpline: SketchSplineObject | SketchStyleSplineObject,
		controlPointPositions: THREE.Vector3[],
		curvePoints: THREE.Vector3[],
		curveMetadata: Parameters<SketchSplineObject['setCurveMetadata']>[0],
		curveEvaluationDefinition?: SketchBSplineEvaluationDefinition,
	): void {
		/**
		 * 普通插值样条的采样点和精确极点是一个不可分割的定义。
		 * 只要投影已携带权威定义，即使控制点数量不变也必须通过对象重建协议原子替换，
		 * 否则显示几何已更新而捕捉与统一参数求解仍使用旧曲线。
		 */
		if (curveEvaluationDefinition) {
			targetSpline.rebuildByCurveDefinition({
				controlPointPositions: controlPointPositions.map(point => point.clone()),
				curvePoints: curvePoints.map(point => point.clone()),
				curveMetadata,
				curveEvaluationDefinition,
			});
			return;
		}

		const currentControlPoints = targetSpline.getControlPoints();
		if (currentControlPoints.length === controlPointPositions.length) {
			currentControlPoints.forEach((point, index) => {
				point.syncPositionSilently(controlPointPositions[index].clone());
			});
			targetSpline.setCurveMetadata(curveMetadata);
			targetSpline.updateCurvePoints(curvePoints.map(point => point.clone()));
			return;
		}

		targetSpline.rebuildByCurveDefinition({
			controlPointPositions: controlPointPositions.map(point => point.clone()),
			curvePoints: curvePoints.map(point => point.clone()),
			curveMetadata,
			curveEvaluationDefinition,
		});
	}

	/**
	 * 判断普通样条的端点对象身份是否支持本轮原位重建。
	 *
	 * 说明：
	 * 1. 与绘制链路一致，闭合样条仍保留首末独立编辑控制点；
	 * 2. 首末存取位误共享时不能原位逐点回写，否则末点坐标会覆盖首点；
	 * 3. 端点身份不兼容时必须走 replacement，不能在原对象上静默合并或拆分点。
	 *
	 * @param targetSpline 当前目标普通样条。
	 * @param projectionResult 本轮普通样条投影结果。
	 * @returns 端点对象身份与闭合语义一致时返回 true。
	 */
	private canRebuildSplineProjectionInPlace(
		targetSpline: SketchSplineObject,
		projectionResult: Extract<ProjectionResult, {projectionResultKind: 'spline'}>,
	): boolean {
		const controlPoints = targetSpline.getControlPoints();
		const startPoint = controlPoints[0];
		const endPoint = controlPoints[controlPoints.length - 1];
		if (!startPoint || !endPoint) {
			return false;
		}

		const targetSharesEndpoint = startPoint === endPoint;
		const targetDeclaresClosed = targetSpline.getCurveMetadata().closureKind
			=== SketchBezierCurveClosureKind.Closed;
		const projectionDeclaresClosed = projectionResult.geometryPayload
			.curveMetadata.closureKind === SketchBezierCurveClosureKind.Closed;
		if (targetDeclaresClosed !== projectionDeclaresClosed) {
			return false;
		}

		return !targetSharesEndpoint;
	}

	/**
	 * 判断目标实体与投影结果是否支持原位重建。
	 *
	 * @param targetEntity 目标实体。
	 * @param projectionResult 最新投影结果。
	 * @returns 是否支持原位重建。
	 */
	private canRebuildProjectionInPlace(targetEntity: Object3D, projectionResult: ProjectionResult): boolean {
		switch (projectionResult.projectionResultKind) {
			case 'line':
				return isSketchLineObject(targetEntity);
			case 'circle':
				return isSketchCircleObject(targetEntity);
			case 'arc':
				return isSketchArcObject(targetEntity);
			case 'ellipse':
			case 'ellipse-arc':
				return isSketchEllipseObject(targetEntity);
			case 'parabola':
				return isSketchParabolaObject(targetEntity);
			case 'spline':
				return isSketchSplineObject(targetEntity)
					&& this.canRebuildSplineProjectionInPlace(targetEntity, projectionResult);
			case 'style-spline':
				return isSketchStyleSplineObject(targetEntity);
		}
	}

	/**
	 * 原位应用投影结果到目标实体。
	 *
	 * @param targetEntity 目标实体。
	 * @param projectionResult 最新投影结果。
	 */
	private applyProjectionInPlace(targetEntity: Object3D, projectionResult: ProjectionResult): void {
		switch (projectionResult.projectionResultKind) {
			case 'line':
				if (!isSketchLineObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的直线对象。');
				}
				this.applyLineProjectionInPlace(targetEntity, projectionResult);
				return;
			case 'circle':
				if (!isSketchCircleObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的圆对象。');
				}
				this.applyCircleProjectionInPlace(targetEntity, projectionResult);
				return;
			case 'arc':
				if (!isSketchArcObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的圆弧对象。');
				}
				this.applyArcProjectionInPlace(targetEntity, projectionResult);
				return;
			case 'ellipse':
			case 'ellipse-arc':
				if (!isSketchEllipseObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的椭圆对象。');
				}
				this.applyEllipseProjectionInPlace(targetEntity, projectionResult);
				return;
			case 'parabola':
				if (!isSketchParabolaObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的抛物线对象。');
				}
				this.applyParabolaProjectionInPlace(targetEntity, projectionResult);
				return;
			case 'spline':
				if (!isSketchSplineObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的普通样条对象。');
				}
				this.applySplineProjectionInPlace(targetEntity, projectionResult);
				return;
			case 'style-spline':
				if (!isSketchStyleSplineObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的样式样条对象。');
				}
				this.applyStyleSplineProjectionInPlace(targetEntity, projectionResult);
		}
	}

	/**
	 * 按 same-kind 语义原位更新目标实体。
	 * 说明：
	 * 1. line / arc / ellipse-arc 不能再直接使用投影 payload 覆写弧段或线段；
	 * 2. 这些类型需要保留目标在投影载体上的附着状态；
	 * 3. circle / ellipse 仍然采用完整几何刷新。
	 *
	 * @param reference 引用记录。
	 * @param targetEntity 目标实体。
	 * @param projectionResult 最新投影结果。
	 * @returns 本轮应写回的投影状态。
	 */
	private applySameKindProjectionInPlace(
		reference: SketchEntityReferenceRecord,
		targetEntity: Object3D,
		projectionResult: ProjectionResult
	): SketchEntityReferenceProjectionState | undefined {
		switch (projectionResult.projectionResultKind) {
			case 'line': {
				if (!isSketchLineObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的直线对象。');
				}
				const persistedProjectionState = this.resolveLineProjectionState(reference, targetEntity);
				const attachmentMode = this.resolveLineProjectionAttachmentMode(reference, persistedProjectionState);
				const projectionState =
					attachmentMode === 'full-entity'
						? this.createFullLineProjectionState(projectionResult, persistedProjectionState)
						: persistedProjectionState;
				this.applyLineProjectionStateInPlace(targetEntity, projectionResult, projectionState);
				return projectionState;
			}
			case 'circle':
				if (!isSketchCircleObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的圆对象。');
				}
				this.applyCircleProjectionInPlace(targetEntity, projectionResult);
				return undefined;
			case 'arc': {
				if (!isSketchArcObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的圆弧对象。');
				}
				const projectionState = this.resolveArcProjectionState(reference, targetEntity);
				this.applyArcProjectionStateInPlace(targetEntity, projectionResult, projectionState);
				return projectionState;
			}
			case 'ellipse':
				if (!isSketchEllipseObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的椭圆对象。');
				}
				this.applyEllipseProjectionInPlace(targetEntity, projectionResult);
				return undefined;
			case 'ellipse-arc': {
				if (!isSketchEllipseObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的椭圆对象。');
				}
				const projectionState = this.resolveEllipseArcProjectionState(reference, targetEntity);
				this.applyEllipseArcProjectionStateInPlace(targetEntity, projectionResult, projectionState);
				return projectionState;
			}
			case 'parabola':
				if (!isSketchParabolaObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的抛物线对象。');
				}
				this.applyParabolaProjectionInPlace(targetEntity, projectionResult);
				return undefined;
			case 'spline':
				if (!isSketchSplineObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的普通样条对象。');
				}
				this.applySplineProjectionInPlace(targetEntity, projectionResult);
				return undefined;
			case 'style-spline':
				if (!isSketchStyleSplineObject(targetEntity)) {
					throw new Error('目标实体不是可原位更新的样式样条对象。');
				}
				this.applyStyleSplineProjectionInPlace(targetEntity, projectionResult);
				return undefined;
		}
	}

	/**
	 * 判断是否可以保持目标对象身份并原位修改。
	 *
	 * @param targetEntity 当前目标对象。
	 * @param projectionResult 最新投影结果。
	 * @returns 可以原位修改时返回 true。
	 */
	public canMutateInPlace(targetEntity: Object3D, projectionResult: ProjectionResult): boolean {
		return this.canRebuildProjectionInPlace(targetEntity, projectionResult);
	}

	/**
	 * 按投影类型原位修改目标几何。
	 *
	 * @param reference 当前引用记录。
	 * @param targetEntity 当前目标对象。
	 * @param projectionResult 最新投影结果。
	 * @returns 需要随记录保存的投影状态。
	 */
	public mutateInPlace(
		reference: SketchEntityReferenceRecord,
		targetEntity: Object3D,
		projectionResult: ProjectionResult
	): SketchEntityReferenceProjectionState | undefined {
		const projectionState = this.applySameKindProjectionInPlace(
			reference,
			targetEntity,
			projectionResult,
		);
		if (targetEntity instanceof AbstractSketchEdgeObject) {
			this.prepareConnectedLineNeighborGeometryRefresh(targetEntity);
			targetEntity.refreshConnectedNeighborGeometries();
		}
		return projectionState;
	}
}
